import { useImmer } from "use-immer";
import { useEffect, useLayoutEffect } from "react";
import { useStoresQueries } from "./useStoresQueries";
import { useCategorySelectChip, usePermission } from "../../../shared";
import { useDebounce } from "use-debounce";

export const useStores = ({ isList, storeId }) => {
  const isStoreAssignMacid = usePermission("store-assign-mac-id");
  const isMacidView = usePermission("store-mac-id-view");
  const isDeviceView = usePermission("store-device-view");
  const isMacidDelete = usePermission("mac-id-delete");

  const [state, setState] = useImmer({
    search: "",
    isStoreRecipe: false,
    isDialogOpen: false,
    selectedStatus: 1,
    selectedType: "",
    chipCategoryId: "",
    deleteMacOpen: false,
    selectedMac: null,
    addMacOpen: false,
    macId: "",
    addMacs: [],
    dataList: [],
    allDataList: [],
    searchKey: "",
    recipeList: [],
    isLoading: false,
  });

  const [debouncedText] = useDebounce(state?.search, 1000);

  const {
    listQuery,
    storeDetails,
    unAssignMac,
    assignMac,
    storeMappedRecipe,
    assignRecipeToStore,
    deleteRecipeFromStore,
    storeMappedAllRecipe,
  } = useStoresQueries({
    isList,
    storeId,
    selectedType: state.selectedType,
    selectedStatus: state.selectedStatus,
    debouncedText,
  });

  const onChangeCategoryHandler = (value) => {
    setState((draft) => {
      draft.chipCategoryId = value;
    });
  };
  const selectChipCategory = useCategorySelectChip({
    categorylist: true,
    onChangeCategoryHandler,
  });
  useEffect(() => {
    const firstId =
      selectChipCategory?.chipCategory?.chipCategoryOptions?.[0]?.id;
    if (state.search) {
      setState((draft) => {
        draft.chipCategoryId = "";
      });
    } else {
      if (firstId && !state.chipCategoryId) {
        setState((draft) => {
          draft.chipCategoryId = firstId;
        });
      }
    }
  }, [
    selectChipCategory?.chipCategory?.chipCategoryOptions,
    state.chipCategoryId,
    state.search,
  ]);
  const onSaveMac = (data) => {
    const payload = {
      mac_id: state.addMacs,
      store_id: storeId,
    };
    assignMac.mutate({
      data: payload,
      handleModal: () => handleModal("add-mac", false),
    });
  };

  const handleModal = (type, state, data) => {
    switch (type) {
      case "add-mac":
        setState((draft) => {
          draft.addMacOpen = state;
          draft.addMacs = [];
          draft.macId = "";
        });
        break;
      case "delete-mac":
        setState((draft) => {
          draft.deleteMacOpen = state;
          draft.selectedMac = data;
        });
        break;
    }
  };

  const removeMac = () => {
    const payload = {
      mac_id: state.selectedMac,
      store_id: storeId,
    };
    unAssignMac.mutate({
      data: payload,
      handleModal: () => handleModal("delete-mac", false, null),
    });
  };

  const onChange = (e) => {
    const { value } = e.target;
    setState((draft) => {
      draft.macId = value;
    });
  };

  const handleAddMacId = () => {
    setState((draft) => {
      draft.addMacs = [...draft.addMacs, draft.macId];
      draft.macId = "";
    });
  };

  const handleDeleteMacTag = (mac) => {
    setState((draft) => {
      draft.addMacs = draft.addMacs.filter((v) => v !== mac);
    });
  };

  useEffect(() => {
    if (listQuery?.data) {
      setState((draft) => {
        draft.dataList = listQuery?.data;
        draft.allDataList = listQuery?.data;
      });
    }
  }, [listQuery.data]);

  const onSearchChange = (string) => {
    setState((draft) => {
      draft.searchKey = string;
      draft.dataList = searchStores(string);
    });
  };

  function searchStores(query) {
    return state.allDataList.filter((item) => {
      const nameMatch = item.name.toLowerCase().includes(query.toLowerCase());
      const macMatch = item.mac_id.some((mac) => mac.mac_id.includes(query));
      return nameMatch || macMatch;
    });
  }

  useLayoutEffect(() => {
    if (storeMappedRecipe?.data || storeMappedAllRecipe?.data) {
      const isDialogOpenData = state?.isDialogOpen
        ? storeMappedAllRecipe?.data?.data
        : storeMappedRecipe?.data?.data;

      const filteredData =
        state.chipCategoryId && Array.isArray(isDialogOpenData)
          ? isDialogOpenData.filter(
              (item) => item.category_id === state.chipCategoryId
            )
          : isDialogOpenData;

      setState((draft) => {
        draft.isLoading = true;
      });

      const recipes = Object?.values(
        filteredData?.reduce((acc, item) => {
          if (!acc[item.subcategory_id]) {
            acc[item.subcategory_id] = {
              id: item.subcategory_id,
              recipe_name: item.subcategory_name,
              subcategory_display_order: item.subcategory_display_order,
              subRows: [],
            };
          }

          acc[item.subcategory_id].subRows.push({
            id: item.id,
            recipe_name: item.name,
            image: item.image,
            status: item.status,
            store: item.stores,
            type: item.type,
            display_order: item.display_order,
          });

          return acc;
        }, {}) || {}
      )
        .sort(
          (a, b) => a.subcategory_display_order - b.subcategory_display_order
        )
        .map((item) => ({
          ...item,
          subRows: item.subRows.sort(
            (a, b) => a.display_order - b.display_order
          ),
        }));

      setState((draft) => {
        draft.recipeList = recipes;
        draft.isLoading = false;
      });
    }
  }, [
    storeMappedRecipe.data,
    state.chipCategoryId,
    state.isDialogOpen,
    storeMappedAllRecipe.data,
  ]);

  const onSearchHandler = (search) => {
    setState((draft) => {
      draft.search = search;
    });
  };

  const onClearFilter = () => {
    setState((draft) => {
      draft.selectedType = "";
      draft.selectedStatus = "";
    });
  };
  const handleOpenDialog = (type) => {
    setState((draft) => {
      draft.isDialogOpen = type;
      draft.isStoreRecipe = type;
    });
  };

  const handleAssignRecipeToStore = (selectedRecipes) => {
    const recipeIds = selectedRecipes;
    const payload = {
      store_id: storeId,
      recipe_ids: recipeIds,
    };
    assignRecipeToStore.mutate({
      data: payload,
      handleModal: () => handleOpenDialog(false),
    });
  };
  const handleDeleteRecipeFromStore = (id) => {
    const payload = {
      store_delete: 1,
    };
    deleteRecipeFromStore.mutate({
      data: payload,
      recipeId: id,
    });
  };
  const recipeChipCategoryValuesHandler = (v) => {
    setState((draft) => {
      draft.chipCategoryId = v?.id;
    });
  };

  return {
    state,
    listQuery,
    storeDetails,
    handleModal,
    removeMac,
    onChange,
    handleAddMacId,
    handleDeleteMacTag,
    onSaveMac,
    onSearchChange,
    isStoreAssignMacid,
    isMacidView,
    isDeviceView,
    isMacidDelete,
    onSearchHandler,
    setState,
    onClearFilter,
    handleOpenDialog,
    handleAssignRecipeToStore,
    assignRecipeToStore,
    selectChipCategory,
    storeMappedRecipe,
    handleDeleteRecipeFromStore,
    recipeChipCategoryValuesHandler,
    storeMappedAllRecipe,
  };
};
